/**
 * SubscriptionSettingsRow — Trigger A (Spec SOS-332 rule 1).
 *
 * The "Subscription" row inside Settings → Plan. State-aware:
 *
 *   free            → "Learn about the Pro plan" + NEW tag      → /subscription
 *   active          → "Manage your Pro plan"                    → /settings/subscription
 *   past_due        → "Attention" amber tag                     → /settings/subscription
 *   canceled_active → "Ends on {date}" amber tag                → /settings/subscription
 *   expired         → "Reactivate your Pro"                     → /subscription
 *   practitioner    → row hidden (Pro is instructor-only)
 *
 * Reuses analytics (origin='settings') and useSubscriptionStatus.
 *
 * Parameterized: renders into a parent's row layout via a single React subtree,
 * matching the existing `SettingsRow` shape. The parent stays in control of
 * spacing / borders. We don't import SettingsRow here — that would create a
 * cross-cutting dependency. Instead we render the equivalent DOM with the same
 * colour tokens.
 */

import React, { useEffect } from 'react';
import { ChevronRight, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/contexts/I18nContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useMyAccountProfile } from '../hooks/useMyAccountProfile';
import { usePremiumTriggerAnalytics } from '../hooks/usePremiumTriggerAnalytics';

const COLORS = {
  textSecondary: '#8D8D8D',
  border:        '#FFFFFF0D',
  amber:         '#F39C12',
  red:           '#D72E16',
};

function formatShortDate(date: Date | null): string {
  if (!date) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export const SubscriptionSettingsRow: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { displayState, subscription, isLoading: subLoading } = useSubscriptionStatus();
  const { isInstructor, isLoading: profileLoading } = useMyAccountProfile();
  const { trackShown, trackClicked } = usePremiumTriggerAnalytics('settings');

  // Spec rule 4: practitioners do NOT see the row at all.
  const shouldRender = isInstructor;

  useEffect(() => {
    if (shouldRender) trackShown();
  }, [shouldRender, trackShown]);

  if (profileLoading || subLoading || !shouldRender) return null;

  // Derive copy + destination + right-side tag from state
  const { subtitleKey, tag, destination } = (() => {
    switch (displayState) {
      case 'active':
        return {
          subtitleKey: 'settings.subscription_row_subtitle_pro',
          tag: null,
          destination: '/settings/subscription',
        };
      case 'past_due':
        return {
          subtitleKey: 'settings.subscription_row_subtitle_pro',
          tag: { label: t('settings.subscription_row_tag_attention'), color: COLORS.amber },
          destination: '/settings/subscription',
        };
      case 'canceled_active':
        return {
          subtitleKey: 'settings.subscription_row_subtitle_pro',
          tag: {
            label: t('settings.subscription_row_tag_ends_on', {
              date: formatShortDate(subscription?.currentPeriodEnd ?? null),
            }),
            color: COLORS.amber,
          },
          destination: '/settings/subscription',
        };
      case 'expired':
        return {
          subtitleKey: 'settings.subscription_row_subtitle_expired',
          tag: null,
          destination: '/subscription',
        };
      case 'none':
      default:
        return {
          subtitleKey: 'settings.subscription_row_subtitle',
          tag: { label: t('settings.subscription_row_tag_new'), color: COLORS.red },
          destination: '/subscription',
        };
    }
  })();

  const onClick = () => {
    trackClicked();
    navigate(destination);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center w-full px-5 py-4 gap-4 text-left border-b"
      style={{ borderColor: COLORS.border }}
    >
      <div className="flex-shrink-0 w-5 h-5" style={{ color: COLORS.red }}>
        <Star className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium text-sm">{t('settings.subscription_row')}</p>
        <p style={{ color: COLORS.textSecondary }} className="text-xs mt-0.5">{t(subtitleKey)}</p>
      </div>
      <div className="flex-shrink-0 flex items-center gap-2">
        {tag && (
          <span
            className="text-[10px] font-semibold tracking-wider uppercase px-2 py-1 rounded-full"
            style={{
              color: tag.color,
              backgroundColor: `${tag.color}22`,
              border: `1px solid ${tag.color}55`,
            }}
          >
            {tag.label}
          </span>
        )}
        <ChevronRight className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
      </div>
    </button>
  );
};
